import React, { useState, useEffect } from 'react';
import {
  Input,
  Textarea,
  Button,
  Spacer,
  RadioGroup,
  DateInput,
  TimeInput,
} from "@heroui/react";
import MapSinglePoint from './MapSinglePoint';
import StyledRadio from './StyledRadio';
import { Listbox, ListboxItem } from "@heroui/react";
import { ListboxWrapper } from './ListBoxWrapper';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem
  } from "@heroui/dropdown";
import { API_URL, AreaData, Gender, getAreas, RecordKind} from '../api/api';
import { booleanPointInPolygon, point as turfPoint } from '@turf/turf';
import {Time, CalendarDate} from "@internationalized/date";


export function dateStrToTime(datetime: string): Time {
  const dt = new Date(datetime);
  return new Time(dt.getHours(), dt.getMinutes(), dt.getSeconds());
}

export function dateStrToDate(datetime: string): CalendarDate {
  const dt = new Date(datetime); 
  return new CalendarDate( dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

export function updateTime(newTime: Time, oldDatetime: string) {
  const dt = new Date(oldDatetime);
  dt.setHours(newTime.hour);
  dt.setMinutes(newTime.minute);
  dt.setSeconds(newTime.second);
  return dt.toISOString();
}
export function updateDate(newDate: CalendarDate, oldDatetime: string) {
  const dt = new Date(oldDatetime);
  dt.setFullYear(newDate.year);
  dt.setMonth(newDate.month -1);
  dt.setDate(newDate.day);
  return dt.toISOString();
}


interface FormData {
  recordKind: RecordKind,
  alias?: string;
  gender?: Gender | '';
  ageRange?: string;
  caseTag?: string[];
  lengthOfInteraction?: string;
  notes?: string;
  lamplightCaseNumber?: string;
  savolo?: string;
  asp?: string;
  reportedBy: string;
  identifiableMarkers?: string;
  referralFrom?: string;
  referralTo?: string;
  createdAt?: string;
  reportedAt?: string;
  lastUpdated?: string;
  location?: { lat: number; lng: number } | null;
  areaId: string | null;
}

const UnifiedRecordForm: React.FC = () => {

    const params = useParams();
    const recordId = params.id;

    const dropdownOptions = [
        { key: 'na', label: 'N/A' },
        { key: 'addiction_services', label: 'Addiction Services' },
        { key: 'area_id', label: "Area"},
        { key: 'bethany', label: 'Bethany' },
        { key: 'crisis', label: 'Crisis' },
        { key: 'essential_edinburgh', label: 'Essential Edinburgh' },
        { key: 'gps', label: 'GP’s' },
        { key: 'hostels', label: 'Hostels' },
        { key: 'housing', label: 'Housing – TAP/Localities' },
        { key: 'internal_cyrenians', label: 'Internal to Cyrenians' },
        { key: 'leap', label: 'LEAP' },
        { key: 'mental_health', label: 'Mental Health' },
        { key: 'police_scotland', label: 'Police Scotland – Essan' },
        { key: 'recovery_services', label: 'Recovery Services (ERA)' },
        { key: 'salvation_army', label: 'Salvation Army' },
        { key: 'shelter', label: 'Shelter' },
        { key: 'simon_community', label: 'Simon Community – Streetwork/rapid access' },
        { key: 'social_bite', label: 'Social Bite and Social Bite Village' },
        { key: 'social_work', label: 'Social Work' },
      ];
      
    
    const [formData, setFormData] = useState<FormData>(
        recordId
            ? {
                recordKind: null,
                alias: '',
                gender: '',
                ageRange: '',
                caseTag: [],
                lengthOfInteraction: '',
                reportedBy: '',
                createdAt: '',
                reportedAt: '',
                lastUpdated: new Date().toISOString(),
                location: null,
                areaId: null
            }
            : {
                recordKind: 'person',
                alias: '',
                gender: '',
                ageRange: '',
                caseTag: [],
                lengthOfInteraction: '',
                reportedBy: '',
                createdAt: new Date().toISOString(),
                reportedAt: new Date().toISOString(),
                lastUpdated: new Date().toISOString(),
                location: null,
                referralFrom: 'na',
                referralTo: 'na',
                areaId: null
            }
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showTick, setShowTick] = useState(false); 
    const [fetchingData, setFetchingData] = useState(true);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [allAreas, setAllAreas] = useState<AreaData[]>();
    const [aliasSetManually, setAliasSetManually] = useState(false);
    const navigate = useNavigate();

    const [selectedKeysDropDownFrom, setSelectedKeysDropDownFrom] = React.useState(new Set(["text"]));
    const [selectedKeysDropDownTo, setSelectedKeysDropDownTo] = React.useState(new Set(["text"]));

    const [selectedLabelDropDownFrom, setSelectedLabelDropDownFrom] = useState<string>('N/A');
    const [selectedLabelDropDownTo, setSelectedLabelDropDownTo] = useState<string>('N/A');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(d => ({ ...d, [name]: value }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(d => ({ ...d, [name]: value }));
  };

  const fetchCognitoToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken as unknown as string || null;
    } catch (err) {
      console.error('Error fetching Cognito token:', err);
      return null;
    }
  };


  const [defaultCoords, setDefaultCoordinates] = useState<{ lat: number; lng: number }>({ lat: 0, lng: 0 });
  // Callback function to update coordinates in the parent component
  const handleCoordinatesChange = (newCoordinates: { lat: number; lng: number }) => {
    setDefaultCoordinates(newCoordinates); // Update coordinates state
    setFormData((prevFormData) => ({
      ...prevFormData,
      location: newCoordinates, // Update only the location field
      areaId: null // reset area
    }));
    console.log('Coordinates in parent:', newCoordinates);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = await fetchCognitoToken();
      if (!token) throw new Error('Cognito token is not available.');

      const endpoint = recordId ? `${API_URL}/records/${recordId}` : `${API_URL}/records`;

      const method = recordId ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error(`Error: ${response.statusText}`);

      setShowTick(true);
      setTimeout(() => {
          setShowTick(false);
          if (!recordId) {
            navigate('/'); // Navigate to the homepage
          }
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const user = await getCurrentUser();
      setFormData((prevFormData) => ({
        ...prevFormData,
        reportedBy: user.signInDetails?.loginId || '',
      }));
    };

    fetchUser();
  }, []);

  useEffect(() => {
    (async () => {
      try {
          const areas = await getAreas(false);
          setAllAreas(areas);
        } catch (error) {
          console.error("Error fetching areas:", error);
      }
    })();
  })

  useEffect(() => {
      if (defaultCoords && allAreas && formData.areaId === null) {
        const autoArea = allAreas.find(area => booleanPointInPolygon(turfPoint([defaultCoords.lng, defaultCoords.lat]), area.Geometry));
        setFormData(d => ({...d, areaId: autoArea?.ID || ''}));
      }
  }, [allAreas, defaultCoords])
  

  useEffect(() => {
    const fetchRecordData = async () => {
      if (!recordId) {
        setFetchingData(false);
        return;
      }

      try {
        const token = await fetchCognitoToken();
        if (!token) throw new Error('Cognito token is not available.');

        const response = await fetch(
          `${API_URL}/records/${recordId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) throw new Error(`Error fetching record data: ${response.statusText}`);

        const data = await response.json();
        setFormData({
            ...formData,
            recordKind: data.record.record_kind,
            alias: data.record.alias,
            gender: data.record.gender,
            ageRange: data.record.age_range,
            notes: data.record.notes,
            lamplightCaseNumber: data.record.lamplight_case_number,
            savolo: data.record.savolo,
            asp: data.record.asp,
            identifiableMarkers: data.record.identifiable_markers,
            caseTag: data.record.case_tag,
            lengthOfInteraction: data.record.length_of_interaction,
            referralFrom: data.record.referral_from,
            referralTo: data.record.referral_to,
            location: {
              lat: data.record.location[0],
              lng: data.record.location[1],
            },
            createdAt: data.record.created_at, // Format date to ISO string,
            reportedBy: data.record.reported_by,
            reportedAt: data.record.reported_at || data.record.created_at,
            areaId: data.record.area_id
        });
  
        if (data.record.alias){
          setAliasSetManually(true);
        }
        setSelectedKeys(new Set(data.record.case_tag));

        setSelectedKeysDropDownFrom(new Set([data.record.referral_from]));
        setSelectedKeysDropDownTo(new Set([data.record.referral_to]));
  
        // Dynamically find the correct labels
        const fromOption = dropdownOptions.find(option => option.key === data.record.referral_from);
        const toOption = dropdownOptions.find(option => option.key === data.record.referral_to);
  
        setSelectedLabelDropDownFrom(fromOption?.label || 'N/A');
        setSelectedLabelDropDownTo(toOption?.label || 'N/A');
      } catch (err) {
        setError('Failed to fetch record data.');
      } finally {
        setFetchingData(false);
      }
    };

    fetchRecordData();
  }, [recordId]);

  useEffect(() => {
    if(aliasSetManually){
      // don't change it
    } else if (formData.recordKind == 'location'){
      const area = allAreas?.find(a => a.ID == formData.areaId);
      setFormData(d => ({...d, alias: area?.Name}));
    } else if (formData.alias) {
      setFormData(d => ({...d, alias: ''})); // reset alias
    }
  }, [formData.recordKind, formData.areaId, formData.alias, allAreas]);

  const handleDelete = async () => {
    if (!recordId) return;
  
    const confirmDelete = window.confirm("Are you sure you want to delete this record?");
    if (!confirmDelete) return;
  
    setLoadingDelete(true);
    setError(null);
  
    try {
      const token = await fetchCognitoToken();
      if (!token) throw new Error('Cognito token is not available.');
  
      const response = await fetch(
        `${API_URL}/records/${recordId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (!response.ok) throw new Error(`Error: ${response.statusText}`);
  
      alert("Record deleted successfully.");
      navigate('/records'); // Redirect to the homepage or desired route
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setLoadingDelete(false);
    }
  };

  if (fetchingData) return <p>Loading...</p>;

  return (
    <form onSubmit={handleSubmit}>
      <MapSinglePoint 
        fixed={!!recordId}
        onCoordinatesChange={handleCoordinatesChange}  
        location={formData.location || null}
        markerData={{RecordKind: formData.recordKind, AgeRange: formData.ageRange, Gender: formData.gender, SAVOLO: formData.savolo}}
        allAreas={allAreas || []} />
    
      <Spacer y={4} />

      <RadioGroup
        aria-label="RecordKind"
        orientation="horizontal"
        className="gap-2"
        value={formData.recordKind || ''}
        onChange={handleRadioChange}
        name="recordKind"
      >
        <StyledRadio value="person">Person</StyledRadio>
        <StyledRadio value="business">Business</StyledRadio>
        <StyledRadio value="location">Location</StyledRadio>
      </RadioGroup>

      <Spacer y={4} />

     <p className='text-sm font-semibold mb-2 mt-2'>Area</p>

      <Dropdown>
        <DropdownTrigger>
          <Button
            className="capitalize w-full"
            variant="shadow"
            style={{
              justifyContent: 'flex-start',
              textAlign: 'left',
            }}
          >
            {allAreas?.find(area => area.ID == formData.areaId)?.Name || 'Other'}
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          className="text-left w-full"
          disallowEmptySelection
          aria-label="Single selection example"
          selectedKeys={formData.areaId ? [formData.areaId] : []}
          selectionMode="single"
          variant="flat"
          onSelectionChange={(sel: any) => {
            setFormData(d => ({ ...d, areaId: sel?.currentKey  }));
          }}
        >
          <ListboxItem key="other">Other</ListboxItem>
          <>{allAreas ? allAreas.map(area => (<DropdownItem key={area.ID} data-key={area.ID}>{area.Name}</DropdownItem>)) : <></>}</>
        </DropdownMenu>
      </Dropdown>

      <Spacer y={4} />

      <p className='text-sm font-semibold mb-2 mt-2'>Alias</p>
      <Input
        aria-label="Alias"
        name="alias"
        required
        variant="bordered"
        onChange={handleChange}
        onInput={() => setAliasSetManually(true)}
        value={formData.alias || ''}
        placeholder={formData.recordKind == 'person'? "Enter an alias" : formData.recordKind == 'business' ? 'Alias of the business' : "Alias of the location"}
      />
      <Spacer y={4} />

      {formData.recordKind == 'person' ? <>
        <p className='text-sm font-semibold mb-2 mt-2'>Gender</p>
        <RadioGroup
          aria-label="Gender"
          orientation="horizontal"
          className="gap-2"
          value={formData.gender || ''}
          onChange={handleRadioChange}
          name="gender"
        >
          <StyledRadio value="male">Male</StyledRadio>
          <StyledRadio value="female">Female</StyledRadio>
          <StyledRadio value="other">Other</StyledRadio>
        </RadioGroup>

        <Spacer y={4} />

        <p className='text-sm font-semibold mb-2 mt-2'>Age Range</p>
        <RadioGroup
          aria-label="Age Range"
          value={formData.ageRange || ''}
          onChange={handleRadioChange}
          name="ageRange"
          orientation="horizontal"
          className="gap-2"
        >
          <StyledRadio value="0-25">&lt; 25</StyledRadio>
          <StyledRadio value="26-49">26-49</StyledRadio>
          <StyledRadio value="50+">50 &gt;</StyledRadio>
        </RadioGroup>

      </> : <></>}

      <Spacer y={4} />

      <p className='text-sm font-semibold mb-2 mt-2'>Case Tags</p>
      <ListboxWrapper>
        <Listbox
          aria-label="Multiple selection for Case Tags"
          selectedKeys={selectedKeys}
          variant="flat"
          selectionMode="multiple"
          onSelectionChange={(keys) => {
            const selectedValues = Array.from(keys) as string[]; // Convert the keys to an array
            setSelectedKeys(new Set(selectedValues)); // Update selectedKeys state
            setFormData(d => ({ ...d, caseTag: selectedValues })); // Update formData.caseTag
          }}
        >
          <ListboxItem key="begging">Begging Spot</ListboxItem>
          <ListboxItem key="drugs">Drug Paraphernalia</ListboxItem>
          <ListboxItem key="bedding">Mattress / Bedding / Sleeping Bag</ListboxItem>
          <ListboxItem key="public_health_issues">Public Health Issue</ListboxItem>
          <ListboxItem key="rough_sleeping">Rough Sleeping</ListboxItem>
          <ListboxItem key="sex_work">Sex Work</ListboxItem>
          <ListboxItem key="tent">Tent</ListboxItem>
          <ListboxItem key="nrpg">No recourse to public funds (NRPG)</ListboxItem>
          <ListboxItem key="veteran">Veteran</ListboxItem>
          <ListboxItem key="uaa">Unable to access accommodation (UAA)</ListboxItem>
        </Listbox>
      </ListboxWrapper>

      <Spacer y={4} />

      <p className='text-sm font-semibold mb-2 mt-2'>Length of Interaction (in minutes)</p>
      <RadioGroup
        aria-label="Length of Interaction"
        value={formData.lengthOfInteraction || ''}
        onChange={handleRadioChange}
        name="lengthOfInteraction"
        orientation="horizontal"
        className=""
      >
          <StyledRadio value="5">0-5</StyledRadio>
          <StyledRadio value="15">6-15</StyledRadio>
          <StyledRadio value="30">16-30</StyledRadio>
          <StyledRadio value="45">31-45</StyledRadio>
          <StyledRadio value="60">45+</StyledRadio>
      </RadioGroup>

      <Spacer y={4} />

      { formData.recordKind == 'person' ? <>  
        <p className='text-sm font-semibold mb-2 mt-2'>Lamplight Case Number</p>
        <Input
        aria-label="Lamplight Case Number"
        name="lamplightCaseNumber"
        type="string"
        onChange={handleChange}
        placeholder="Enter a case number"
        value={formData.lamplightCaseNumber || ''}
        />

        <Spacer y={4} />

        <p className='text-sm font-semibold mb-2 mt-2'>SAVOLO</p>
        <RadioGroup
        aria-label="SAVOLO"
        value={formData.savolo || ''}
        onChange={handleRadioChange}
        name="savolo"
        orientation="horizontal"
        className="gap-2"
        >
        <StyledRadio value="yes">Yes</StyledRadio>
        <StyledRadio value="no">No</StyledRadio>
        <StyledRadio value="unknown">Unknown</StyledRadio>
        </RadioGroup>

        <Spacer y={4} />

        <p className='text-sm font-semibold mb-2 mt-2'>ASP</p>
        <RadioGroup
        aria-label="ASP"
        value={formData.asp || ''}
        onChange={handleRadioChange}
        name="asp"
        orientation="horizontal"
        className="gap-2"
        >
        <StyledRadio value="yes">Yes</StyledRadio>
        <StyledRadio value="no">No</StyledRadio>
        <StyledRadio value="unknown">Unknown</StyledRadio>
        </RadioGroup>

        <Spacer y={4} />


        <p className='text-sm font-semibold mb-2 mt-2'>Identifiable Markers</p>
        <Textarea
          aria-label="Identifiable Markers"
          name="identifiableMarkers"
          onChange={handleChange}
          placeholder="Enter identifiable markers"
          value={formData.identifiableMarkers || ''}
        />

        <Spacer y={4} />
      </> : <></>}

        <p className='text-sm font-semibold mb-2 mt-2'>Referral From</p>
        <Dropdown>
            <DropdownTrigger>
                <Button
                    className="capitalize w-full"
                    variant="shadow"
                    style={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    }}
                >
                    {selectedLabelDropDownFrom}
                </Button>
            </DropdownTrigger>
            <DropdownMenu
                className="text-left w-full"
                disallowEmptySelection
                aria-label="Single selection example"
                selectedKeys={selectedKeysDropDownFrom}
                selectionMode="single"
                variant="flat"
                onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys)[0] as string;
                    const selectedItem = dropdownOptions.find(option => option.key === selectedKey);
                    setSelectedLabelDropDownFrom(selectedItem?.label || 'Other');
                    setFormData((prevFormData) => ({
                    ...prevFormData,
                    referralFrom: selectedKey,
                    }));
                }}
                >
                {dropdownOptions.map(option => (
                    <DropdownItem key={option.key} data-key={option.key}>
                    {option.label}
                    </DropdownItem>
                ))}
                </DropdownMenu>
      </Dropdown>

      <Spacer y={4} />

      <p className='text-sm font-semibold mb-2 mt-2'>Referral To</p>
      <Dropdown>
      <DropdownTrigger>
        <Button
            className="capitalize w-full"
            variant="shadow"
            style={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            }}
        >
            {selectedLabelDropDownTo}
        </Button>
        </DropdownTrigger>
        <DropdownMenu
                className="text-left w-full"
                disallowEmptySelection
                aria-label="Single selection example"
                selectedKeys={selectedKeysDropDownTo}
                selectionMode="single"
                variant="flat"
                onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys)[0] as string;
                    const selectedItem = dropdownOptions.find(option => option.key === selectedKey);
                    setSelectedLabelDropDownTo(selectedItem?.label || 'N/A');
                    setFormData((prevFormData) => ({
                    ...prevFormData,
                    referralTo: selectedKey,
                    }));
                }}
                >
                {dropdownOptions.map(option => (
                    <DropdownItem key={option.key} data-key={option.key}>
                    {option.label}
                    </DropdownItem>
                ))}
                </DropdownMenu>
      </Dropdown>

        <Spacer y={4} />

        <p className='text-sm font-semibold mb-2 mt-2'>Notes</p>
        <Textarea
        aria-label="Notes"
        name="notes"
        onChange={handleChange}
        placeholder="Enter notes"
        value={formData.notes || ''}
        />

        <Spacer y={4} />

        <p className="text-sm font-semibold mb-2 mt-2">Reported by</p>
        <Input
        aria-label="Reporter"
        name="reportedBy"
        disabled
        variant="bordered"
        value={formData.reportedBy || ''}
        placeholder="Reported By"
        />


        <Spacer y={4} />
        <p className="text-sm font-semibold mb-2 mt-2">Reported at
          {formData.createdAt == formData.reportedAt ? <></> : <span className='ml-2 text-xs text-gray-500'>(Created at {new Date(formData.createdAt || '').toLocaleString("en-GB")})</span>}
        </p>
       
       <div style={{ display: 'flex', justifyContent: 'left', marginTop: '1rem', flexDirection: 'row' }}>
       <DateInput
         name='reportedAtDate'
         aria-label='Reported Date'
         className="max-w-sm"
         value={dateStrToDate(formData.reportedAt || '')} 
        onChange={(d: CalendarDate | null) => {
            d && setFormData(dd => ({...dd, reportedAt: updateDate(d, dd.reportedAt || '') }))
          }}
       />
        <TimeInput  
          name='reportedAtTime'
          aria-label='Reported At'
          className="max-w-sm ml-2"
          value={dateStrToTime(formData.reportedAt || '')}
          onChange={(t: Time | null) => {
            t && setFormData(d => ({...d, reportedAt: updateTime(t, d.reportedAt || '') }))
          }}
          />
        </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
        <Button type="submit" disabled={loading} color='primary'>
          {loading ? (recordId ? 'Updating...' : 'Submitting...') : recordId ? 'Update' : 'Submit'}
        </Button>

        {recordId && (
          <Button
            type="button"
            disabled={loadingDelete}
            color="danger"
            onClick={handleDelete}
          >
            {loadingDelete ? 'Deleting...' : 'Delete'}
          </Button>
        )}
      </div>

      
      {showTick && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent background
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999, // Ensure it overlays everything
          }}
        >
          <div
            style={{
              color: 'green',
              fontSize: '20rem',
              textAlign: 'center',
            }}
          >
            ✓
          </div>
        </div>
      )}
      <Spacer y={2} />
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <Spacer y={8} />
    </form>
  );
};

export default UnifiedRecordForm;